import { useEffect, useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { api } from "services/api.service";
import { useAlert } from "hooks/useAlert";
import { LogisticsTicket } from "types/logisticsTicket";
import { JobDetailViewModel } from "./jobDetailTypes";
import { DetailCard } from "./DetailCard";

type Props = {
  job: JobDetailViewModel;
  onTicketUpdated?: (ticket: LogisticsTicket) => void;
};

export default function InternalNotesCard({ job, onTicketUpdated }: Props) {
  const { alertSuccess, alertError } = useAlert();
  const savedNotes = job.internalNotes ?? "";
  const [notes, setNotes] = useState(savedNotes);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setNotes(savedNotes);
  }, [savedNotes]);

  const handleSave = () => {
    setSaving(true);

    api
      .patch<LogisticsTicket>(`/logistics_tickets/${job.ticketUuid}/internal_notes`, { internalNotes: notes })
      .then(({ data }) => {
        onTicketUpdated?.(data);
        alertSuccess("Internal notes updated");
      })
      .catch(() => {
        alertError("Unable to update internal notes. Please try again.");
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <DetailCard title="Internal Notes" style={{ marginBottom: 4 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mt: -1.5, mb: 2 }}>
        Only visible to the Revivn team. Not shared with customers or vendors.
      </Typography>
      <TextField
        multiline
        minRows={3}
        fullWidth
        placeholder="Add notes for the logistics team..."
        value={notes}
        onChange={(event) => setNotes(event.target.value)}
        disabled={saving}
      />
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1.5 }}>
        <Button variant="text" disabled={saving || notes === savedNotes} onClick={() => setNotes(savedNotes)}>
          Cancel
        </Button>
        <Button variant="contained" disabled={saving || notes === savedNotes} onClick={handleSave}>
          Save
        </Button>
      </Box>
    </DetailCard>
  );
}
